import { Table2 } from 'lucide-react';
import { useEffect, useId, useState, type ReactNode } from 'react';
import { Area, AreaChart, ResponsiveContainer, YAxis, type TooltipProps } from 'recharts';
import { cn } from '@/lib/cn';
import { useAppStore } from '@/lib/store';
import { Button } from './button';
import { Card, CardDescription, CardHeader, CardTitle } from './card';

const TOKEN_NAMES = ['chart-1', 'chart-2', 'chart-3', 'chart-4', 'chart-5', 'chart-6', 'chart-7', 'chart-8'] as const;
const read = (style: CSSStyleDeclaration, name: string, fallback: string): string => {
  const ch = style.getPropertyValue(`--${name}`).trim();
  return ch ? `rgb(${ch.split(/\s+/).join(', ')})` : fallback;
};

export interface ChartTheme {
  readonly series: readonly string[];
  readonly grid: string;
  readonly text: string;
  readonly muted: string;
  readonly surface: string;
  readonly positive: string;
  readonly negative: string;
  readonly primary: string;
}

const computeTheme = (): ChartTheme => {
  if (typeof document === 'undefined' || typeof getComputedStyle !== 'function') {
    return { series: ['#0b6b6b'], grid: '#d5dde1', text: '#10242b', muted: '#4b616b', surface: '#fff', positive: '#16794a', negative: '#c0322b', primary: '#0b6b6b' };
  }
  const s = getComputedStyle(document.documentElement);
  return {
    series: TOKEN_NAMES.map((n) => read(s, n, '#0b6b6b')),
    grid: read(s, 'border', '#d5dde1'),
    text: read(s, 'text', '#10242b'),
    muted: read(s, 'text-muted', '#4b616b'),
    surface: read(s, 'surface-raised', '#fff'),
    positive: read(s, 'positive', '#16794a'),
    negative: read(s, 'negative', '#c0322b'),
    primary: read(s, 'primary', '#0b6b6b'),
  };
};

/**
 * Resolved chart colours from tokens (SVG attributes can't read CSS variables). Re-computes on theme change.
 * Use `theme.series[i]` for categorical series — always in order, never cherry-picked.
 */
export const useChartTheme = (): ChartTheme => {
  const pref = useAppStore((s) => s.theme);
  const [theme, setTheme] = useState(computeTheme);
  useEffect(() => {
    const update = () => setTheme(computeTheme());
    update();
    const mq = typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-color-scheme: dark)') : null;
    mq?.addEventListener('change', update);
    return () => mq?.removeEventListener('change', update);
  }, [pref]);
  return theme;
};

/** Shared axis props: muted 12px ticks, no axis lines. Spread onto `<XAxis>` / `<YAxis>`. */
export const useAxisProps = () => {
  const t = useChartTheme();
  return { tick: { fill: t.muted, fontSize: 12 }, axisLine: false, tickLine: false, stroke: t.grid } as const;
};

/** Tooltip content with token styling. `format` renders each value (e.g. money). */
export const ChartTooltip = ({
  active, payload, label, format = (v) => String(v), labelFormat = (l) => String(l),
}: TooltipProps<number, string> & { readonly format?: (v: number) => string; readonly labelFormat?: (l: unknown) => string }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-border bg-surface-raised px-3 py-2 text-xs shadow-md">
      {/* pie/treemap tooltips have no category label — the slice name is the row */}
      {label !== undefined && label !== '' ? <p className="mb-1 font-medium text-text">{labelFormat(label)}</p> : null}
      {payload.map((p) => (
        <p key={`${String(p.dataKey)}-${String(p.name)}`} className="flex items-center gap-2 tabular-nums text-text-muted">
          <span className="size-2 rounded-full" style={{ background: p.color ?? (p.payload as { fill?: string } | undefined)?.fill }} aria-hidden />
          <span>{p.name}</span>
          <span className="ml-auto font-medium text-text">{typeof p.value === 'number' ? format(p.value) : ''}</span>
        </p>
      ))}
    </div>
  );
};

export interface SparklineProps {
  readonly data: readonly number[];
  /** Accessible description, e.g. "Net worth rose 4% over 30 days". */
  readonly label: string;
  readonly height?: number;
  readonly tone?: 'primary' | 'positive' | 'negative' | 'auto';
  readonly className?: string;
}

/** Tiny trend line with a soft fill. Decorative chart + text label for assistive tech. */
export const Sparkline = ({ data, label, height = 40, tone = 'auto', className }: SparklineProps) => {
  const t = useChartTheme();
  const gid = useId().replace(/:/g, '');
  const first = data[0] ?? 0;
  const last = data[data.length - 1] ?? 0;
  const colour = tone === 'primary' ? t.primary : tone === 'positive' ? t.positive : tone === 'negative' ? t.negative : last >= first ? t.primary : t.negative;
  const points = data.map((v, i) => ({ i, v }));
  return (
    <div role="img" aria-label={label} className={cn('w-full', className)} style={{ height }}>
      {data.length > 1 ? (
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={points} margin={{ top: 2, right: 0, bottom: 2, left: 0 }}>
            <defs>
              <linearGradient id={`sg${gid}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={colour} stopOpacity={0.25} />
                <stop offset="100%" stopColor={colour} stopOpacity={0} />
              </linearGradient>
            </defs>
            <YAxis hide domain={['dataMin', 'dataMax']} />
            <Area type="monotone" dataKey="v" stroke={colour} strokeWidth={2} fill={`url(#sg${gid})`} isAnimationActive={false} dot={false} />
          </AreaChart>
        </ResponsiveContainer>
      ) : null}
    </div>
  );
};

export interface ChartCardProps {
  readonly title: ReactNode;
  readonly description?: ReactNode;
  /** One-sentence takeaway read by screen readers (and shown under the title when `showSummary`). */
  readonly summary: string;
  readonly showSummary?: boolean;
  /** Optional tabular fallback: header row + rows, toggled with "View as table". */
  readonly table?: { readonly columns: readonly string[]; readonly rows: readonly (readonly ReactNode[])[] };
  readonly actions?: ReactNode;
  /** Legend items (colour + label) rendered consistently above the chart. */
  readonly legend?: readonly { readonly label: string; readonly color: string }[];
  readonly height?: number;
  readonly className?: string;
  readonly children: ReactNode;
}

/** Card wrapper for every chart: title, legend, accessible summary, optional data-table view. */
export const ChartCard = ({ title, description, summary, showSummary, table, actions, legend, height = 260, className, children }: ChartCardProps) => {
  const [asTable, setAsTable] = useState(false);
  return (
    <Card className={className}>
      {/* actions (range tabs, table toggle) wrap under the title on narrow cards instead of squeezing it */}
      <CardHeader className="flex-wrap">
        <div className="min-w-0 flex-1 basis-60">
          <CardTitle>{title}</CardTitle>
          {description ? <CardDescription>{description}</CardDescription> : null}
          {showSummary ? <p className="mt-1 text-sm text-text">{summary}</p> : null}
        </div>
        <div className="flex flex-wrap items-center gap-1">
          {actions}
          {table ? (
            <Button variant="ghost" size="sm" icon={<Table2 aria-hidden />} aria-pressed={asTable} onClick={() => setAsTable((v) => !v)}>
              {asTable ? 'View chart' : 'View as table'}
            </Button>
          ) : null}
        </div>
      </CardHeader>
      {legend?.length ? (
        <ul className="flex flex-wrap gap-x-4 gap-y-1 px-5 pb-2 text-xs text-text-muted">
          {legend.map((l) => (
            <li key={l.label} className="flex items-center gap-1.5">
              <span className="size-2.5 rounded-sm" style={{ background: l.color }} aria-hidden />
              {l.label}
            </li>
          ))}
        </ul>
      ) : null}
      <div className="px-3 pb-4">
        {asTable && table ? (
          <div className="max-h-80 overflow-auto px-2" tabIndex={0} role="region" aria-label={`${typeof title === 'string' ? title : 'Chart'} data`}>
            <table className="w-full text-sm">
              <thead>
                <tr>
                  {table.columns.map((c) => (
                    <th key={c} className="border-b border-border py-2 text-left text-xs font-medium text-text-muted">
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {table.rows.map((r, i) => (
                  <tr key={i}>
                    {r.map((cell, j) => (
                      <td key={j} className="border-b border-border py-2 tabular-nums">
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <figure style={{ height }} aria-label={summary} role="img" className="m-0">
            {/* the figure's label (and "View as table") carry the data; the SVG's own marks stay out of the a11y tree */}
            <div aria-hidden className="h-full">{children}</div>
          </figure>
        )}
      </div>
      {!showSummary ? <p className="sr-only">{summary}</p> : null}
    </Card>
  );
};
